// Runs a whole video through the upscaler: plays it, enlarges every frame on the graphics card, and records the result (with the original sound) to a new file.
// It happens at the speed of the video: browsers can only record a video while it plays.
import { Upscaler } from "./gl";
import { bitrateFor, fpsFromTimes } from "./plan";

const loaded = (video) => new Promise((resolve, reject) => {
  if (video.readyState >= 2) { resolve(); return; }
  video.onloadeddata = () => resolve();
  video.onerror = () => reject(new Error("This browser cannot open that video. Use an MP4 (H.264), WebM or MOV file."));
});

// Reads a video file: its size, length and a first frame. The element is returned so that it can be used for a preview. Rejects when it cannot be opened.
export async function openVideo(file) {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.src = url;
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  try {
    await loaded(video);
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
  if (!video.videoWidth || !video.videoHeight) { URL.revokeObjectURL(url); throw new Error("That file has no picture, or its picture cannot be read by this browser."); }
  if (!isFinite(video.duration)) {                              // a file made by a browser's recorder has no length written in it: seeking far past the end makes the browser find it
    await new Promise((resolve) => {
      const done = () => { video.removeEventListener("timeupdate", done); resolve(); };
      video.addEventListener("timeupdate", done);
      video.currentTime = 1e7;
      setTimeout(done, 3000);
    });
    video.currentTime = 0;
    await new Promise((resolve) => { video.onseeked = resolve; setTimeout(resolve, 1000); });
  }
  const seconds = isFinite(video.duration) ? video.duration : 0;
  return { video, url, w: video.videoWidth, h: video.videoHeight, seconds };
}

// The frame rate of an opened video, found by playing a second of it (silently); 0 when the browser cannot tell.
export function detectFps(video) {
  if (typeof video.requestVideoFrameCallback !== "function") return Promise.resolve(0);
  return new Promise((resolve) => {
    const times = [];
    const wasMuted = video.muted;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      video.pause();
      video.muted = wasMuted;
      video.currentTime = 0;
      resolve(fpsFromTimes(times));
    };
    const next = () => video.requestVideoFrameCallback((now, meta) => { times.push(meta.mediaTime); if (times.length >= 40) finish(); else next(); });
    video.muted = true;
    video.currentTime = 0;
    next();
    video.play().catch(finish);
    setTimeout(finish, 1800);
  });
}

// options: { file, size: [w, h], look (given to the engine's render with each frame), makeEngine(canvas) (default: the upscaler), bitrateScale, mime, fps, onProgress(0..1), onCanvas(canvas) (the picture being made, for showing), signal: { cancelled } }
// Resolves with { blob, w, h, seconds, frames (drawn), presented (shown by the browser, 0 if unknown), hiddenSeconds }.
export async function upscaleVideo({ file, size, look, mime, fps = 30, makeEngine = null, bitrateScale = 1, onProgress = () => {}, onCanvas = () => {}, signal = {} }) {
  const { video, url, seconds } = await openVideo(file);
  video.muted = false;                                          // its sound goes into the recording, not to the speakers (see below)
  video.volume = 1;
  const canvas = document.createElement("canvas");
  let up;
  let context;
  let recorder;
  const cleanup = () => {
    try { video.pause(); } catch { /* already stopped */ }
    if (context) context.close().catch(() => {});
    if (up) up.dispose();
    URL.revokeObjectURL(url);
    video.removeAttribute("src");
    video.load();
  };
  try {
    up = makeEngine ? makeEngine(canvas) : new Upscaler(canvas);          // (the noise reducer brings its own picture engine)
    up.setOutput(size[0], size[1]);
    context = new (window.AudioContext || window.webkitAudioContext)();
    await context.resume();
    const sound = context.createMediaStreamDestination();
    context.createMediaElementSource(video).connect(sound);     // (not connected to the speakers: the processing is silent)
    const stream = canvas.captureStream(0);
    const track = stream.getVideoTracks()[0];
    const manual = typeof track.requestFrame === "function";
    const source = manual ? stream : canvas.captureStream(fps);
    const tracks = [...source.getVideoTracks(), ...sound.stream.getAudioTracks()];
    recorder = new MediaRecorder(new MediaStream(tracks), { mimeType: mime, videoBitsPerSecond: Math.round(bitrateFor(size[0], size[1], fps) * bitrateScale), audioBitsPerSecond: 192000 });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    const stopped = new Promise((resolve, reject) => {
      recorder.onstop = () => resolve(new Blob(chunks, { type: mime.split(";")[0] }));
      recorder.onerror = (e) => reject(e.error || new Error("Recording failed."));
    });

    let frames = 0;
    let lastFrameAt = performance.now();
    let lastReport = 0;
    let hiddenSeconds = 0;
    let hiddenSince = 0;
    const onVisibility = () => {
      if (document.hidden) hiddenSince = performance.now();
      else if (hiddenSince) { hiddenSeconds += (performance.now() - hiddenSince) / 1000; hiddenSince = 0; }
    };
    document.addEventListener("visibilitychange", onVisibility);
    // An engine may need a moment before it can draw a frame (the background changer asks an AI model about it first): `prepare` is awaited, and a frame that
    // arrives while the last one is still being made is skipped. A failure of the engine stops the job.
    let drawing = false;
    let failure = null;
    const draw = async () => {
      if (drawing) return;
      drawing = true;
      try {
        if (up.prepare) await up.prepare(video, look);
        up.render(video, look);
        if (manual) track.requestFrame();
        frames += 1;
        lastFrameAt = performance.now();
        if (lastFrameAt - lastReport > 250) { lastReport = lastFrameAt; onProgress(seconds ? Math.min(1, video.currentTime / seconds) : 0); }     // (the page need not redraw its bar at every frame)
      } catch (e) {
        failure = e;
      } finally {
        drawing = false;
      }
    };
    // The browser says when it has a new frame in two ways: a callback for each frame shown, and a running count of the frames decoded. The callback is skipped when the page is
    // busy or the screen refreshes slowly, so the count is watched too: a frame is drawn as soon as either says that there is a new one, and never twice.
    const decoded = () => (typeof video.getVideoPlaybackQuality === "function" ? video.getVideoPlaybackQuality().totalVideoFrames : -1);
    let seen = decoded();
    const drawIfNew = () => { const count = decoded(); if (count === -1 || count !== seen) { seen = count; draw(); } };

    video.currentTime = 0;
    await new Promise((resolve) => { if (video.currentTime === 0 && video.readyState >= 2) resolve(); else video.onseeked = () => resolve(); });
    await draw();
    if (failure) throw failure;
    seen = decoded();
    onCanvas(canvas);
    recorder.start(1000);
    const finished = new Promise((resolve) => { video.onended = resolve; });
    const timers = {};
    const live = () => !video.paused && !video.ended && !signal.cancelled;
    if (typeof video.requestVideoFrameCallback === "function") {
      const next = () => { video.requestVideoFrameCallback(() => { if (live()) { next(); drawIfNew(); } }); };
      next();
    }
    timers.poll = setInterval(() => { if (live() && (decoded() !== -1 || typeof video.requestVideoFrameCallback !== "function")) drawIfNew(); }, decoded() !== -1 ? 8 : Math.round(1000 / fps));
    await video.play();
    await new Promise((resolve, reject) => {
      finished.then(resolve);
      timers.watch = setInterval(() => {
        if (failure) reject(failure);
        else if (signal.cancelled) resolve();
        else if (!video.ended && !document.hidden && performance.now() - lastFrameAt > 20000) reject(new Error("The video stopped playing while it was being processed. Please try again."));
      }, 500);
    }).finally(() => { clearInterval(timers.poll); clearInterval(timers.watch); document.removeEventListener("visibilitychange", onVisibility); });
    if (hiddenSince) hiddenSeconds += (performance.now() - hiddenSince) / 1000;
    if (!signal.cancelled) { await draw(); onProgress(1); }
    await new Promise((r) => setTimeout(r, 200));
    if (recorder.state !== "inactive") recorder.stop();
    const blob = await stopped;
    if (signal.cancelled) throw new Error("cancelled");
    return { blob, w: size[0], h: size[1], seconds, frames, presented: Math.max(0, decoded()), hiddenSeconds: Math.round(hiddenSeconds * 10) / 10 };
  } finally {
    if (recorder && recorder.state !== "inactive") { try { recorder.stop(); } catch { /* already stopped */ } }
    cleanup();
  }
}

export const processVideo = upscaleVideo;
